import type { ButtonHTMLAttributes } from 'react';
import { useCallback } from 'react';
import './Tabs.css';

export interface TabConfig {
  id: string;
  label: string;
  disabled?: boolean;
  buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

interface TabsProps {
  tabs: TabConfig[];
  activeTabId: string;
  onTabSelect?: (tabId: string) => void;
  className?: string;
  tablistLabel?: string;
  ariaLabelledBy?: string;
}

const composeClassName = (...tokens: Array<string | false | null | undefined>): string =>
  tokens.filter(Boolean).join(' ');

export const Tabs = ({
  tabs,
  activeTabId,
  onTabSelect,
  className,
  tablistLabel,
  ariaLabelledBy
}: TabsProps) => {
  const handleSelect = useCallback(
    (tabId: string, disabled?: boolean) => {
      if (disabled) {
        return;
      }
      onTabSelect?.(tabId);
    },
    [onTabSelect]
  );

  return (
    <div className={composeClassName('tabs', className)}>
      <div
        className="tabs__tablist"
        role="tablist"
        aria-label={tablistLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              aria-disabled={tab.disabled ?? false}
              tabIndex={tab.disabled ? -1 : isActive ? 0 : -1}
              className={composeClassName(
                'tabs__tab',
                isActive && 'tabs__tab--active',
                tab.disabled && 'tabs__tab--disabled'
              )}
              onClick={() => handleSelect(tab.id, tab.disabled)}
              disabled={tab.disabled}
              {...tab.buttonProps}
            >
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
